import { bestDoctors } from '../data/bestDoctors';

// Utility function to convert doctor name to URL-friendly slug
export const doctorNameToSlug = (doctorName) => {
  return doctorName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .trim();
};

// Utility function to convert slug back to display name (for reverse lookup)
export const slugToDoctorName = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
};

// Get distinct specialities for a specific country from local doctor data
export const getDoctorSpecialitiesForCountry = (country = 'India') => {
  const countryDoctors = bestDoctors.filter(
    doctor => doctor.country === country && doctor.specialty && doctor.specialty.trim()
  );

  const allSpecialities = countryDoctors.map(doctor => doctor.specialty);
  const uniqueSpecialities = [...new Set(allSpecialities)];

  // If no doctors found for this country, return fallback specialities
  if (uniqueSpecialities.length === 0) {
    const fallbackSpecialities = [
      'Cardiology',
      'Oncology',
      'Neurology',
      'Neurosurgery',
      'Gastroenterology',
      'Orthopedics',
      'Nephrology',
      'Urology',
      'Gynecology',
      'Pulmonology',
      'Endocrinology',
      'Pediatrics',
      'Dermatology',
      'Ophthalmology',
      'ENT',
      'Plastic Surgery',
      'General Surgery'
    ];
    
    return fallbackSpecialities.map(spec => `Best ${spec} Doctors in ${country}`);
  }

  // Map specialities to display format - keep simple mapping since specialties are stored simply
  const specialityMap = {
    'Cardiology': 'Cardiology',
    'Cardiac Sciences': 'Cardiology',
    'Oncology': 'Oncology',
    'Cancer': 'Oncology',
    'Neurology': 'Neurology',
    'Neurosurgery': 'Neurosurgery', 
    'Gastroenterology': 'Gastroenterology',
    'Orthopaedics': 'Orthopedics',
    'Orthopedics': 'Orthopedics',
    'Joint Replacement': 'Orthopedics',
    'Nephrology': 'Nephrology',
    'Urology': 'Urology',
    'Gynecology': 'Gynecology',
    'Obstetrics': 'Gynecology',
    'Pulmonology': 'Pulmonology',
    'Respiratory Medicine': 'Pulmonology',
    'Endocrinology': 'Endocrinology',
    'Diabetes Care': 'Endocrinology',
    'Pediatrics': 'Pediatrics',
    'NICU': 'Pediatrics',
    'Dermatology': 'Dermatology',
    'Cosmetology': 'Dermatology',
    'Ophthalmology': 'Ophthalmology',
    'ENT (Ear, Nose, Throat)': 'ENT',
    'ENT': 'ENT',
    'Plastic Surgery': 'Plastic Surgery',
    'Reconstructive Surgery': 'Plastic Surgery',
    'General Surgery': 'General Surgery',
    'Laparoscopic Surgery': 'General Surgery',
    'Emergency Medicine': 'Emergency Medicine',
    'Emergency Care': 'Emergency Medicine',
    'Internal Medicine': 'Internal Medicine',
    'Radiology': 'Radiology',
    'Pathology': 'Pathology',
    'Psychiatry': 'Psychiatry',
    'Anesthesiology': 'Anesthesiology',
    'Vascular Surgery': 'Vascular Surgery',
    'Spine Surgery': 'Spine Surgery',
    'Robotic Surgery': 'Robotic Surgery',
    'Transplant Surgery': 'Transplant Surgery',
    'IVF & Fertility': 'IVF & Fertility',
    'Fertility': 'IVF & Fertility',
    'Dialysis': 'Dialysis',
    'ICU': 'ICU',
    'Rehabilitation': 'Rehabilitation',
    'Physiotherapy': 'Physiotherapy'
  };

  const formattedSpecialities = uniqueSpecialities
    .map(spec => {
      const mappedName = specialityMap[spec] || spec;
      return `Best ${mappedName} Doctors in ${country}`;
    })
    .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
    .sort(); // Sort alphabetically

  return formattedSpecialities;
};

// Get doctors by speciality and country from local data
export const getDoctorsBySpeciality = (speciality, country = 'India') => {
  // Extract the actual speciality from the display name
  const extractSpeciality = (displaySpeciality) => {
    const match = displaySpeciality.match(/Best (.+) Doctors in/);
    if (!match) return [];
    
    const spec = match[1];
    
    // Map display names back to database specialities - simplified since we store simple names
    const reverseMap = {
      'Cardiology': ['Cardiology', 'Cardiac Sciences'],
      'Oncology': ['Oncology', 'Cancer'],
      'Neurology': ['Neurology'],
      'Neurosurgery': ['Neurosurgery'],
      'Gastroenterology': ['Gastroenterology'],
      'Orthopedics': ['Orthopaedics', 'Orthopedics', 'Joint Replacement'],
      'Nephrology': ['Nephrology'],
      'Urology': ['Urology'],
      'Gynecology': ['Gynecology', 'Obstetrics'],
      'Pulmonology': ['Pulmonology', 'Respiratory Medicine'],
      'Endocrinology': ['Endocrinology', 'Diabetes Care'],
      'Pediatrics': ['Pediatrics', 'NICU'],
      'Dermatology': ['Dermatology', 'Cosmetology'],
      'Ophthalmology': ['Ophthalmology'],
      'ENT': ['ENT (Ear, Nose, Throat)', 'ENT'],
      'Plastic Surgery': ['Plastic Surgery', 'Reconstructive Surgery'],
      'General Surgery': ['General Surgery', 'Laparoscopic Surgery'],
      'Emergency Medicine': ['Emergency Medicine', 'Emergency Care'],
      'Internal Medicine': ['Internal Medicine'],
      'Radiology': ['Radiology'],
      'Pathology': ['Pathology'],
      'Psychiatry': ['Psychiatry'],
      'Anesthesiology': ['Anesthesiology'],
      'Vascular Surgery': ['Vascular Surgery'],
      'Spine Surgery': ['Spine Surgery'],
      'Robotic Surgery': ['Robotic Surgery'],
      'Transplant Surgery': ['Transplant Surgery'],
      'IVF & Fertility': ['IVF & Fertility', 'Fertility'],
      'Dialysis': ['Dialysis'],
      'ICU': ['ICU'],
      'Rehabilitation': ['Rehabilitation'],
      'Physiotherapy': ['Physiotherapy']
    };
    
    return reverseMap[spec] || [spec];
  };

  const searchSpecialities = extractSpeciality(speciality);
  
  // Filter doctors by country and speciality
  const filteredDoctors = bestDoctors.filter(doctor => 
    doctor.country === country &&
    doctor.specialty &&
    searchSpecialities.some(spec => 
      doctor.specialty.toLowerCase().includes(spec.toLowerCase())
    )
  );

  // Format doctors for display
  return filteredDoctors.map(doctor => ({
    id: doctor.id,
    name: doctor.name,
    specialty: doctor.specialty,
    experience: doctor.experience,
    image: doctor.image || '/images/doctor-placeholder.jpg',
    description: doctor.description || doctor.about || 'Experienced medical professional providing top-quality healthcare services.',
    qualifications: doctor.qualifications || [],
    hospital: doctor.hospital || 'Leading Medical Center',
    country: doctor.country,
    whatsappLink: `https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+I+am+interested+in+consulting+Dr.+${encodeURIComponent(doctor.name)}+for+${encodeURIComponent(speciality)}.+Please+contact+me.&type=phone_number&app_absent=0`,
    link: `/doctor-details/${doctor.id || doctorNameToSlug(doctor.name)}`
  }));
};
